import type { StateLawsListResult } from 'lib/queries/state-law';
import React from 'react';
import Image from 'next/image';
import { Paragraph, Wrap } from '@leafwell/components';
import LinkBadge from './link-badge';
import LineCounter from './line-counter';
import { useWordPressContext } from 'contexts/wordpress-provider';
import { useEventsTracker } from 'contexts/eventsTracker';

export function getStatusColor(variant: string): string {
  switch (variant) {
    case 'secondary':
      return 'lilac-300';
    case 'light':
      return 'green-200';
    case 'dark':
      return 'black';
    default:
      return 'green';
  }
}

export const StateLawsStatusLabel: React.FC<{
  name: string;
  count?: number;
  total: number;
  variant: string;
  className?: string;
  tagName?: React.ElementType;
}> = ({ name, count = 0, total, variant, className, tagName }) => (
  <Paragraph
    size="small"
    className={[
      'grid grid-cols-inline-form justify-between gap-x-6 gap-y-1',
      className,
    ].join(' ')}
    tagName={tagName}
  >
    {name}
    <span>{count}</span>
    <LineCounter
      className="col-span-full"
      color={getStatusColor(variant)}
      count={count}
      total={total}
    />
  </Paragraph>
);

const StateLawsList: React.FC<{
  lawStatuses: StateLawsListResult;
}> = ({ lawStatuses }) => {
  const { translations } = useWordPressContext();
  const { trackEvent } = useEventsTracker();
  let totalLaws = 0;

  for (const status in lawStatuses) {
    totalLaws += lawStatuses[status].items.length;
  }

  return (
    <>
      <Wrap
        spacer={false}
        className="md:grid-cols-2 lg:grid-cols-3 md:gap-x-6 xl:gap-x-12 !gap-y-6 p-6"
      >
        <Paragraph size="small" className="col-span-full">
          {translations['Choose your state in the US']}
          <Image
            src="/us-flag.svg"
            className="w-4 ml-2 inline-block align-baseline"
            alt=""
            width={16}
            height={9}
          />
        </Paragraph>
        {Object.keys(lawStatuses).map(status => {
          const { name, variant, items } = lawStatuses[status];
          return (
            <article key={status} className="grid gap-y-3 content-start">
              <StateLawsStatusLabel
                count={items.length}
                name={name}
                total={totalLaws}
                variant={variant}
                tagName="header"
              />
              <ul className="text-sm flex flex-wrap -ml-2">
                {items.map(({ state, uri }) => (
                  <li
                    className="pr-2 pb-2"
                    key={uri}
                    onClick={() =>
                      trackEvent({
                        event: 'select_content',
                        content_type: translations[state],
                        item_id: 'cannabis_marijuana_laws',
                        origin: 'cannabis_marijuana_laws',
                      })
                    }
                  >
                    <LinkBadge href={uri}>{translations[state]}</LinkBadge>
                  </li>
                ))}
              </ul>
            </article>
          );
        })}
      </Wrap>
    </>
  );
};

export default StateLawsList;
